import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationStatus } from '@shared/enums/notification-status';
import { CrudOperation } from '@shared/models';
import {
  AppNotificationService,
  ModalService,
  UtilityService,
} from '@shared/services';
import { QueryOptions } from 'odata-query';
import { ExportCountriesInformationModalComponent } from '../../components/export-countries-information-modal/export-countries-information-modal.component';
import {
  Collection,
  GridItemCreateEvent,
  InvestmentJurisdiction,
} from '../../models';
import { InvestmentJurisdictionService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-investment-jurisdictions-page',
  templateUrl: 'investment-jurisdictions-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestmentJurisdictionsPageComponent {
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly modalService = inject(ModalService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly utilityService = inject(UtilityService);
  private readonly translateService = inject(TranslateService);

  public investmentJurisdiction: WritableSignal<
    Collection<InvestmentJurisdiction>
  > = signal({
    total: 0,
    items: [],
  });

  public query: Partial<QueryOptions<InvestmentJurisdiction>> = {};

  private investmentJurisdictionsTitle = this.translateService.instant(
    'admin.dashboard.investmentJurisdictionsTitle'
  );

  create(
    investmentJurisdictionGridItem: GridItemCreateEvent<InvestmentJurisdiction>
  ): void {
    let isSuccess = false;

    this.investmentJurisdictionService
      .create(investmentJurisdictionGridItem.item)
      .subscribe(() => {
        investmentJurisdictionGridItem.onSuccess();
        isSuccess = true;

        this.fetchInvestmentJurisdictions();

        this.appNotificationService.crudNotify(
          CrudOperation.Create,
          this.investmentJurisdictionsTitle,
          isSuccess
        );
      });
  }

  update(
    investmentJurisdictionGridItem: GridItemCreateEvent<InvestmentJurisdiction>
  ): void {
    let isSuccess = false;

    this.investmentJurisdictionService
      .update(investmentJurisdictionGridItem.item)
      .subscribe(() => {
        investmentJurisdictionGridItem.onSuccess();
        isSuccess = true;
        this.fetchInvestmentJurisdictions();

        this.appNotificationService.crudNotify(
          CrudOperation.Update,
          this.investmentJurisdictionsTitle,
          isSuccess
        );
      });
  }

  queryUpdated(
    investmentJurisdictionGridItem: Partial<
      QueryOptions<InvestmentJurisdiction>
    >
  ): void {
    this.query = investmentJurisdictionGridItem;
    this.fetchInvestmentJurisdictions();
  }

  exportCountryInformation(countryIds: number[]): void {
    if (countryIds.length === 0) {
      return;
    }
    this.investmentJurisdictionService
      .exportCountryInformation(countryIds)
      .subscribe({
        next: file => {
          const fileName =
            file.fileName ?? this.buildExportFallbackFileName(countryIds);
          this.utilityService.download(fileName, file.data);
          this.appNotificationService.notify(
            'admin.grid.exportCountryInformationSuccessTitle',
            '',
            NotificationStatus.SUCCESS
          );
        },
        error: () => {
          this.appNotificationService.notify(
            'admin.grid.exportCountryInformationErrorTitle',
            'admin.grid.exportCountryInformationErrorDescription',
            NotificationStatus.ERROR
          );
        },
      });
  }

  exportAllCountryInformation(): void {
    this.modalService.open(
      ExportCountriesInformationModalComponent,
      {
        appModalId: 'admin-export-countries-information-modal',
      },
      {
        query: this.query,
        totalRows: this.investmentJurisdiction().total,
      }
    );
  }

  private buildExportFallbackFileName(countryIds: number[]): string {
    const stamp = this.exportFileNameTimestamp();
    if (countryIds.length > 1) {
      return `Country Information-${stamp}.zip`;
    }
    const countryName = this.countryNameForExport(countryIds[0]);
    return `${countryName}-Country Information-${stamp}.docx`;
  }

  private countryNameForExport(countryId: number): string {
    const row = this.investmentJurisdiction().items.find(
      j => j.country?.countryId === countryId
    );
    const raw = row?.name?.trim() || row?.country?.name?.trim() || 'Country';
    return this.sanitizeFileNameSegment(raw);
  }

  private sanitizeFileNameSegment(value: string): string {
    return value
      .replace(/[<>:"/\\|?*]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 150);
  }

  /** Safe for use in download filenames (no slashes or colons). */
  private exportFileNameTimestamp(): string {
    const d = new Date();
    const pad: (n: number) => string = (n: number): string =>
      String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  }

  private fetchInvestmentJurisdictions(): void {
    this.investmentJurisdictionService
      .get({ ...this.query, count: true })
      .subscribe(
        (
          investmentJurisdictionCollection: Collection<InvestmentJurisdiction>
        ) => {
          this.investmentJurisdiction.set(investmentJurisdictionCollection);
        }
      );
  }
}
